<!--
  LoginForm.vue
  Vue 3 authentication component for user login

  Features:
  - Email and password validation
  - Loading states during API calls
  - Error message display
  - Success event emission
  - Preserves vanilla CSS classes for styling

  Props: None

  Emits:
  - success: When login is successful, passes user data
  - switchToRegister: When user clicks "Create Account"

  Usage:
  <LoginForm @success="handleLoginSuccess" @switchToRegister="showRegister" />
-->

<script setup lang="ts">
import { ref, computed } from 'vue'
import GoogleSignIn from './GoogleSignIn.vue'
import LinkAccountModal from './LinkAccountModal.vue'

// API URL configuration (matches vanilla JS)
const isDevPort = window.location.port && !['80', '443', ''].includes(window.location.port)
const API_URL = (window.location.hostname === 'localhost' && isDevPort)
  ? `${window.location.protocol}//localhost:8000/api`
  : `${window.location.origin}/api`

// Component state
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const loading = ref(false)
const errorMessage = ref('')

// Emits
const emit = defineEmits<{
  success: [user: any]
  switchToRegister: []
  forgotPassword: []
}>()

// Validation
const emailError = computed(() => {
  if (!email.value) return ''
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailPattern.test(email.value) ? '' : 'Invalid email format'
})

const passwordError = computed(() => {
  if (!password.value) return ''
  return password.value.length >= 8 ? '' : 'Password must be at least 8 characters'
})

const isFormValid = computed(() => {
  return email.value && password.value && !emailError.value && !passwordError.value
})

/**
 * Handle form submission
 * Validates inputs and calls login API
 */
async function handleSubmit() {
  if (!isFormValid.value) {
    errorMessage.value = 'Please fill in all fields correctly'
    return
  }

  errorMessage.value = ''
  loading.value = true

  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.value,
        password: password.value,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      // Handle error response
      let errorMsg = 'Login failed'
      if (typeof data.detail === 'string') {
        errorMsg = data.detail
      } else if (Array.isArray(data.detail)) {
        errorMsg = data.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ')
      } else if (data.detail && typeof data.detail === 'object') {
        errorMsg = data.detail.msg || data.detail.message || JSON.stringify(data.detail)
      } else if (data.message) {
        errorMsg = data.message
      }
      throw new Error(errorMsg)
    }

    // Store tokens in localStorage
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)

    // Emit success event with user data
    emit('success', data.user)
  } catch (err: any) {
    errorMessage.value = err.message || 'Login failed. Please try again.'
  } finally {
    loading.value = false
  }
}

/**
 * Handle switch to register form
 */
function handleSwitchToRegister() {
  emit('switchToRegister')
}

// ============== Google OAuth ==============

// Account linking state
const showLinkModal = ref(false)
const linkEmail = ref('')
const linkCredential = ref('')

/**
 * Handle successful Google login
 */
function handleGoogleSuccess(user: unknown) {
  emit('success', user)
}

/**
 * Handle Google login requiring account linking
 */
function handleGoogleLinkRequired(email: string, credential: string) {
  linkEmail.value = email
  linkCredential.value = credential
  showLinkModal.value = true
}

/**
 * Handle Google login error
 */
function handleGoogleError(message: string) {
  errorMessage.value = message
}

/**
 * Handle successful account linking
 */
function handleLinkSuccess(user: unknown) {
  showLinkModal.value = false
  emit('success', user)
}

/**
 * Handle account linking cancelled
 */
function handleLinkCancel() {
  showLinkModal.value = false
  linkEmail.value = ''
  linkCredential.value = ''
}
</script>

<template>
  <div class="login-container">
    <form id="login-form" @submit.prevent="handleSubmit">
      <div class="form-group">
        <label for="login-email">Email</label>
        <input
          id="login-email"
          v-model="email"
          type="email"
          required
          :disabled="loading"
          autocomplete="email"
          placeholder="you@example.com"
        />
        <span v-if="emailError" class="error-text">{{ emailError }}</span>
      </div>

      <div class="form-group">
        <label for="login-password">Password</label>
        <div class="password-input-wrapper">
          <input
            id="login-password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            required
            :disabled="loading"
            autocomplete="current-password"
            minlength="8"
            placeholder="Enter your password"
          />
          <button
            type="button"
            class="password-toggle"
            @click="showPassword = !showPassword"
            tabindex="-1"
            :aria-label="showPassword ? 'Hide password' : 'Show password'"
          >
            <span class="material-symbols-outlined">
              {{ showPassword ? 'visibility_off' : 'visibility' }}
            </span>
          </button>
        </div>
        <span v-if="passwordError" class="error-text">{{ passwordError }}</span>
      </div>

      <div class="forgot-password-row">
        <button
          type="button"
          class="forgot-password-link"
          :disabled="loading"
          @click="emit('forgotPassword')"
        >
          Forgot Password?
        </button>
      </div>

      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <button type="submit" class="btn-primary login-btn" :disabled="!isFormValid || loading">
        <span v-if="loading">Logging in...</span>
        <span v-else>Sign In</span>
      </button>
    </form>

    <div class="divider">
      <span>or</span>
    </div>

    <!-- Google Sign-In -->
    <GoogleSignIn
      @success="handleGoogleSuccess"
      @link-required="handleGoogleLinkRequired"
      @error="handleGoogleError"
    />

    <div class="register-prompt">
      <span>Don't have an account?</span>
      <button
        type="button"
        class="register-link"
        :disabled="loading"
        @click="handleSwitchToRegister"
      >
        Create one
      </button>
    </div>
  </div>

  <!-- Account Linking Modal -->
  <LinkAccountModal
    v-if="showLinkModal"
    :email="linkEmail"
    :google-credential="linkCredential"
    @success="handleLinkSuccess"
    @cancel="handleLinkCancel"
  />
</template>

<style scoped>
.login-container {
  display: flex;
  flex-direction: column;
}

#login-form {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

#login-form .form-group {
  margin-bottom: 0;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper input {
  flex: 1;
  padding-right: 44px;
}

.password-toggle {
  position: absolute;
  right: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.5);
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.password-toggle:hover {
  color: rgba(255, 255, 255, 0.8);
}

.password-toggle .material-symbols-outlined {
  font-size: 1.25rem;
}

.error-text {
  display: block;
  color: #ef4444;
  font-size: 0.8125rem;
  margin-top: 0.25rem;
}

.error-message {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #fca5a5;
  padding: 0.625rem 0.875rem;
  border-radius: 8px;
  font-size: 0.8125rem;
}

.login-btn {
  margin-top: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  letter-spacing: 0.025em;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 0.75rem 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.divider span {
  padding: 0 0.75rem;
  color: var(--text-muted, rgba(255, 255, 255, 0.4));
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.register-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.register-prompt span {
  color: var(--text-muted, rgba(255, 255, 255, 0.5));
  font-size: 0.8125rem;
}

.register-link {
  background: transparent;
  border: none;
  color: var(--accent-primary, #d4a72c);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.register-link:hover {
  color: var(--accent-hover, #e5b933);
  background: rgba(212, 167, 44, 0.1);
}

.register-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.forgot-password-row {
  display: flex;
  justify-content: flex-end;
  margin-top: -0.25rem;
  margin-bottom: 0.5rem;
}

.forgot-password-link {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8125rem;
  cursor: pointer;
  padding: 0.25rem 0;
  transition: color 0.2s;
}

.forgot-password-link:hover {
  color: var(--accent-primary, #d4a72c);
}

.forgot-password-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
